'use client';

import { cn } from '@/shared/lib/cn';

interface SettingsCardProps {
  children: React.ReactNode;
  title?: string;
  /** Optional trailing element on the header row, opposite the title —
   *  e.g. a section total and/or a "+" add action (Apple parity: the
   *  estimate sections render a plus opposite the section name). */
  headerAction?: React.ReactNode;
  /** Optional footer caption rendered in muted text below the card.
   *  Mirrors Apple `Section { ... } footer: { Text(...) }`. */
  footer?: string;
  className?: string;
  /** Override the inner surface background/styling. Defaults to the shared
   *  `bg-settings-card` panel tint; the estimate document surfaces pass
   *  `bg-estimate-paper` so the estimate reads as a cream paper sheet. */
  surfaceClassName?: string;
}

/**
 * Settings card component - iOS/Android style grouped settings panel
 * Used for grouping related settings items together
 */
export function SettingsCard({
  children,
  title,
  headerAction,
  footer,
  className,
  surfaceClassName,
}: SettingsCardProps) {
  // Apple parity: section title sits ABOVE the rounded card in sentence
  // case (`Controls`, `References`, `Presentation`) using the iOS Form
  // section-header look — small body size, secondary grey, regular weight,
  // inset from the card by the standard 16-px gutter. Card body stays the
  // rounded surface grouping the rows below — light-gray tint, no drop
  // shadow, mirroring macOS Form `.grouped` System-Settings cards.
  return (
    <div className={cn('space-y-1.5', className)}>
      {(title || headerAction) && (
        <div className="flex items-center justify-between gap-2 px-4 pt-1">
          {title && (
            <h3 className="text-section-subtitle text-muted-foreground">
              {title}
            </h3>
          )}
          {headerAction}
        </div>
      )}
      <div
        className={cn(
          'bg-settings-card rounded-2xl overflow-hidden',
          surfaceClassName
        )}
      >
        {children}
      </div>
      {footer && (
        <p className="text-card-meta text-muted-foreground px-4 pt-1">
          {footer}
        </p>
      )}
    </div>
  );
}
